import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  OnChanges,
  ChangeDetectorRef,
  AfterViewChecked
} from '@angular/core';
import {FormBuilder, FormGroup, Validators, AbstractControl} from '@angular/forms';
import {EquineService} from '../equine.service';
import {QuoteService} from '../../quote.service';
import {capitalizeHorseName, requireEquineQuoteId} from '../equine-utils';
import { isEquineRestrictedCoverSelected } from '../equine-premium-tbc.util';
import {MatSnackBar} from '@angular/material/snack-bar';
import {formatDate} from '@angular/common';

@Component({
  selector: 'app-equine-horse-details',
  templateUrl: './equine-horse-details.component.html',
  styleUrls: ['./equine-horse-details.component.css', '../../quote.component.css']
})
export class EquineHorseDetailsComponent implements OnInit, OnChanges, AfterViewChecked {
  @Input() staticData;
  @Input() modelObject: any = {};
  @Output() modelObjectUpdated = new EventEmitter<any>();

  formFields: FormGroup;
  submitted = false;
  ownershipStatuses: any = [];
  yearsOfBirth: number[] = [];
  calculatedAge: number = null;
  ageDisplay: string = '';
  minAcquiredDate: Date = null;
  maxAcquiredDate: Date = new Date();
  showSumInsuredOver50kReferralMessage = false;
  showRestrictedSumInsuredOver20kReferralMessage = false;

  constructor(
    private formBuilder: FormBuilder,
    private equineService: EquineService,
    private quoteService: QuoteService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.generateYearOptions();
    this.loadOwnershipStatuses();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.modelObject && this.modelObject && Object.keys(this.modelObject).length > 0) {
      this.patchFormValues();
    }
    if (changes.staticData && this.staticData) {
      this.loadOwnershipStatuses();
    }
  }

  ngAfterViewChecked() {
    this.cdr.detectChanges();
  }

  initializeForm() {
    this.formFields = this.formBuilder.group({
      stable_name: ['', [Validators.required, this.capitalizeValidator]],
      passport_name: ['', [Validators.required, this.capitalizeValidator]],
      acquired_date: ['', Validators.required],
      ownership_status: ['', Validators.required],
      purchase_price: ['', [Validators.required, this.wholeNumberValidator]],
      sum_insured: ['', [Validators.required, this.wholeNumberValidator]],
      horse_birth_year: ['', Validators.required],
      age: [{value: '', disabled: true}]
    });

    // Set up value change listeners
    this.formFields.get('horse_birth_year').valueChanges.subscribe(() => {
      this.calculateAge();
    });

    this.formFields.get('ownership_status').valueChanges.subscribe(() => {
      this.onOwnershipStatusChange();
    });

    this.formFields.get('acquired_date').valueChanges.subscribe(() => {
      this.onAcquiredDateChange();
      this.validateSumInsured();
    });

    this.formFields.get('purchase_price').valueChanges.subscribe(() => {
      this.validateSumInsured();
    });

    this.formFields.get('sum_insured').valueChanges.subscribe(() => {
      this.validateSumInsured();
    });

    this.formFields.get('stable_name').valueChanges.subscribe((value) => {
      const formatted = capitalizeHorseName(value);
      this.formFields.get('stable_name').setValue(formatted, { emitEvent: false });
    });

    this.formFields.get('passport_name').valueChanges.subscribe((value) => {
      const formatted = capitalizeHorseName(value);
      this.formFields.get('passport_name').setValue(formatted, { emitEvent: false });
    });
  }

  generateYearOptions() {
    const currentYear = new Date().getFullYear();
    this.yearsOfBirth = [];
    for (let i = 0; i < 40; i++) {
      this.yearsOfBirth.push(currentYear - i);
    }
  }

  loadOwnershipStatuses() {
    if (this.staticData && this.staticData.ownership_statuses) {
      this.ownershipStatuses = this.staticData.ownership_statuses;
    }
  }

  calculateAge() {
    const yearOfBirth = this.formFields.get('horse_birth_year').value;
    if (!yearOfBirth) {
      this.calculatedAge = null;
      this.ageDisplay = '';
      this.formFields.get('age').setValue('');
      return;
    }

    const currentYear = new Date().getFullYear();
    this.calculatedAge = currentYear - yearOfBirth;
    
    if (this.calculatedAge === 0) {
      this.ageDisplay = '<1';
      this.formFields.get('age').setValue('<1');
    } else {
      this.ageDisplay = this.calculatedAge.toString();
      this.formFields.get('age').setValue(this.calculatedAge);
    }
  }

  onAcquiredDateChange() {
    const acquiredDate = this.formFields.get('acquired_date').value;
    if (!acquiredDate) {
      // Clear validation errors if date is cleared
      const errors = this.formFields.get('acquired_date').errors;
      if (errors) {
        delete errors['acquiredAfterCover'];
        if (Object.keys(errors).length === 0) {
          this.formFields.get('acquired_date').setErrors(null);
        } else {
          this.formFields.get('acquired_date').setErrors(errors);
        }
      }
      return;
    }

    // Get cover start date from modelObject - check multiple possible paths
    let coverStartDate = null;
    if (this.modelObject.policy_start_date) {
      coverStartDate = this.modelObject.policy_start_date;
    } else if (this.modelObject.period_of_cover_from) {
      // Fallback for old field name
      coverStartDate = this.modelObject.period_of_cover_from;
    } else if (this.modelObject.quote_details && this.modelObject.quote_details.policy_start_date) {
      coverStartDate = this.modelObject.quote_details.policy_start_date;
    } else if (this.modelObject.quote_details && this.modelObject.quote_details.period_of_cover_from) {
      // Fallback for old field name
      coverStartDate = this.modelObject.quote_details.period_of_cover_from;
    }

    if (coverStartDate) {
      const coverDate = new Date(coverStartDate);
      const acquired = new Date(acquiredDate);
      
      // Reset time to midnight for accurate date comparison
      coverDate.setHours(0, 0, 0, 0);
      acquired.setHours(0, 0, 0, 0);
      
      if (acquired > coverDate) {
        const currentErrors = this.formFields.get('acquired_date').errors || {};
        this.formFields.get('acquired_date').setErrors({
          ...currentErrors,
          'acquiredAfterCover': true
        });
      } else {
        const errors = this.formFields.get('acquired_date').errors;
        if (errors) {
          delete errors['acquiredAfterCover'];
          if (Object.keys(errors).length === 0) {
            this.formFields.get('acquired_date').setErrors(null);
          } else {
            this.formFields.get('acquired_date').setErrors(errors);
          }
        }
      }
    }

    this.validateSumInsured();
  }

  onOwnershipStatusChange() {
    const ownershipStatus = this.formFields.get('ownership_status').value;
    const purchasePriceControl = this.formFields.get('purchase_price');

    // Check if ownership status is 'Purchased' or 'Joint Owned'
    if (ownershipStatus && this.ownershipStatuses.length > 0) {
      const ownershipObj = this.ownershipStatuses.find(s => s.id === ownershipStatus);
      const joinOrOwner = ['Joint Owned', 'Purchased']

      if(ownershipObj && joinOrOwner.includes(ownershipObj.status_name)) {
        purchasePriceControl.setValue(0);
        purchasePriceControl.disable();
      } else {
        purchasePriceControl.enable();
      }

    } else if (!ownershipStatus) {
      // If no status selected, enable the field
      purchasePriceControl.enable();
    }

    this.validateSumInsured();
  }

  public validateSumInsured() {
    const sumInsured = this.formFields.get('sum_insured').value;
    const purchasePrice = this.formFields.get('purchase_price').value;
    const acquiredDate = this.formFields.get('acquired_date').value;
    const ownershipStatus = this.formFields.get('ownership_status').value;
    const quoteType = 1;
    
    if (!sumInsured && sumInsured !== 0) {
      this.showSumInsuredOver50kReferralMessage = false;
      this.showRestrictedSumInsuredOver20kReferralMessage = false;
      return;
    }

    const si = Number(sumInsured);
    const restricted = isEquineRestrictedCoverSelected(this.modelObject, this.staticData);
    this.showSumInsuredOver50kReferralMessage = false;
    this.showRestrictedSumInsuredOver20kReferralMessage = false;
    if (Number.isFinite(si)) {
      if (restricted && si > 20000) {
        this.showRestrictedSumInsuredOver20kReferralMessage = true;
      } else if (!restricted && si > 50000) {
        this.showSumInsuredOver50kReferralMessage = true;
      }
    }

    const errors: any = {};
    const currentErrors = this.formFields.get('sum_insured').errors || {};

    // Rule 2 & 3: Only apply if quote type is New Business
    if (quoteType === 1) {
      if (acquiredDate && ownershipStatus && (purchasePrice !== null && purchasePrice !== undefined)) {
        const acquired = new Date(acquiredDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        acquired.setHours(0, 0, 0, 0);
        const daysDiff = Math.floor((today.getTime() - acquired.getTime()) / (1000 * 60 * 60 * 24));


        // Check if acquired within last 60 days
        if (daysDiff >= 0 && daysDiff <= 60) {
          const ownershipObj = this.ownershipStatuses.find(s => s.id === ownershipStatus);

          if (ownershipObj && ownershipObj.status_name.toLowerCase() === 'purchased') {
            // Rule 2: Sum Insured cannot exceed purchase price
            if (si > purchasePrice) {
              errors['sumInsuredExceedsPurchase'] = true;
            }

            // Rule 3: Sum Insured cannot be more than 50% lower than purchase price
            const minSumInsured = purchasePrice * 0.5;
            if (si < minSumInsured) {
              errors['sumInsuredTooLow'] = true;
            }
          }
        }
      }
    }

    // Merge with existing errors (like required, notWholeNumber)
    const finalErrors = { ...currentErrors, ...errors };
    delete finalErrors['sumInsuredOverLimit'];
    if (!errors['sumInsuredExceedsPurchase']) delete finalErrors['sumInsuredExceedsPurchase'];
    if (!errors['sumInsuredTooLow']) delete finalErrors['sumInsuredTooLow'];

    if (Object.keys(finalErrors).length > 0) {
      this.formFields.get('sum_insured').setErrors(finalErrors);
    } else {
      this.formFields.get('sum_insured').setErrors(null);
    }
  }

  capitalizeValidator(control: AbstractControl): { [key: string]: any } | null {
    if (!control.value) {
      return null;
    }
    // Split by spaces and hyphens
    const words = control.value.split(/[\s-]+/);
    for (const word of words) {
      if (word && word.length > 0) {
        // Check if first character is uppercase
        const firstChar = word[0];
        if (firstChar !== firstChar.toUpperCase()) {
          return { 'notCapitalized': true };
        }
        // Allow multiple capital letters (like XVII) - no additional validation needed
      }
    }
    return null;
  }

  wholeNumberValidator(control: AbstractControl): { [key: string]: any } | null {
    if (!control.value && control.value !== 0) {
      return null;
    }
    const value = Number(control.value);
    if (isNaN(value) || value < 0 || !Number.isInteger(value)) {
      return { 'notWholeNumber': true };
    }
    return null;
  }

  get f(): { [key: string]: AbstractControl } {
    return this.formFields.controls;
  }

  patchFormValues() {
    if (this.modelObject.horse_details) {
      const horseDetails = this.modelObject.horse_details;
      
      if (horseDetails.stable_name) {
        this.formFields.get('stable_name').setValue(horseDetails.stable_name);
      }
      if (horseDetails.passport_name) {
        this.formFields.get('passport_name').setValue(horseDetails.passport_name);
      }
      if (horseDetails.acquired_date) {
        this.formFields.get('acquired_date').setValue(new Date(horseDetails.acquired_date));
      }
      if (horseDetails.ownership_status) {
        this.formFields.get('ownership_status').setValue(horseDetails.ownership_status);
        this.onOwnershipStatusChange();
      }
      if (horseDetails.purchase_price !== undefined) {
        this.formFields.get('purchase_price').setValue(horseDetails.purchase_price);
      }
      if (horseDetails.sum_insured !== undefined) {
        this.formFields.get('sum_insured').setValue(horseDetails.sum_insured);
      }
      if (horseDetails.horse_birth_year) {
        this.formFields.get('horse_birth_year').setValue(horseDetails.horse_birth_year);
        this.calculateAge();
      }
      this.validateSumInsured();
    }
  }

  onSubmit() {
    this.submitted = true;

    const quoteId = requireEquineQuoteId(this.modelObject);
    if (!quoteId) {
      this._snackBar.open('No quote ID found. Please save Quote Details first.', 'Close', { duration: 3000 });
      return;
    }

    if (this.formFields.invalid) {
      return;
    }

    const formValue = this.formFields.getRawValue();
    const data = {
      ...this.modelObject,
      id: quoteId,
      has_form_changed: this.formFields.dirty,
      horse_details: {
        stable_name: formValue.stable_name,
        passport_name: formValue.passport_name,
        acquired_date: formValue.acquired_date ? formatDate(formValue.acquired_date, 'yyyy-MM-dd', 'en') : null,
        ownership_status: formValue.ownership_status,
        purchase_price: formValue.purchase_price,
        sum_insured: formValue.sum_insured,
        horse_birth_year: formValue.horse_birth_year,
        age: formValue.age
      }
    };

    this.equineService.saveQuote(data)
      .subscribe(
        (response: any) => {
          if (response.success) {
            this._snackBar.open('Horse details saved successfully', 'Close', {
              duration: 3000
            });
            this.modelObjectUpdated.emit(response.result);
          }
        },
        (error) => {
          this._snackBar.open('Error saving horse details', 'Close', {
            duration: 3000
          });
        }
      );
  }
}
